import { CheckCircle, ErrorOutline, ExpandMore } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import ErrorMessage from "../../../common/js/components/ErrorMessage";
import SaveAndExitButton from "../../../common/js/components/SaveAndExitButton";
import { LeftChevron } from "../../../common/js/icons";
import ComplianceRulesByDocumentTypeContainer from "./ComplianceRulesByDocumentTypeContainer";
import DocumentTypeAdminDataGrid from "./DocumentTypeAdminDataGrid";
import operations from "./ducks/operations";

const StyledPageToolbar = styled("div")(() => ({
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
    marginTop: "20px",
}));

const StyledGoBackLink = styled("span")({
    textDecoration: "none",
    "&:hover": {
        cursor: "pointer",
        textDecoration: "none",
    },
});

const StyledDeleteButton = styled(Button)(() => ({
    backgroundColor: red[500],
    color: "white",
    "&:hover": {
        backgroundColor: red[800],
    },
}));

const StyledComplianceRules = styled(AccordionDetails)(() => ({
    paddingTop: "0px",
}));

const StyledLoadingSpinner = styled("span")(() => ({
    position: "absolute",
    left: "50%",
    top: "60%",
    transform: "translate(-50%, -50%)",
    marginLeft: "50px",
}));

const DocumentTypeAdmin = (props) => {
    const {
        errors,
        documentTypeId,
        handleSaveAll,
        handleDeleteDocumentType,
        getUserTypes,
        getParentEntityTypeStatuses,
        getUserTypeParentEntityStatuses,
    } = props;

    const navigate = useNavigate();
    const [documentType, setDocumentType] = useState({});
    const [parentEntityTypesList, setParentEntityTypesList] = useState([]);
    const [parentEntityTypesToOptionsMap, setParentEntityTypesToOptionsMap] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [knownDocument, setKnownDocument] = useState(true);
    const [newDocumentName, setNewDocumentName] = useState("");
    const [isExisting, setIsExisting] = useState(true);
    const [userTypes, setUserTypes] = useState([]);
    const [selectedUserType, setSelectedUserType] = useState({});
    const [parentEntityTypeStatuses, setParentEntityTypeStatuses] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const location = useLocation();
    const subHeaderText = isExisting ? "Edit Document" : "Create Document";
    const deleteCancelText = isExisting ? "Delete" : "Cancel";
    const parentEntityTypeKey = documentType?.parentEntityType?.parentEntityTypeKey ?? "";

    useEffect(() => {
        if (userTypes.length > 0 && !selectedUserType?.userTypeKey) {
            setSelectedUserType(userTypes[0]);
        }
    }, [userTypes]);

    const loadDTSRData = async () => {
        const parentEntityTypes = await operations.getAllParentEntityTypes();

        const optionsMap = [
            { value: "", label: "Select A Parent Type", disabled: true },
            ...parentEntityTypes.map((entity) => ({
                value: entity.parentEntityTypeKey,
                label: entity.name,
                disabled: false,
            })),
        ];

        let docTypes = {};
        if (documentTypeId) {
            docTypes = await operations.getDocumentType(documentTypeId);
            const parentEntityTypeKey = docTypes?.parentEntityType?.parentEntityTypeKey;
            if (parentEntityTypeKey) {
                await loadUserTypes(parentEntityTypeKey);
            }
        } else {
            docTypes = { multipleAllowed: false };
            setIsExisting(false);
        }

        setParentEntityTypesList(parentEntityTypes);
        setParentEntityTypesToOptionsMap(optionsMap);
        setDocumentType(docTypes);
        setNewDocumentName(docTypes.name);

        if (docTypes === "ERROR") {
            setKnownDocument(false);
        }
        setDataLoaded(true);
    };

    useEffect(() => {
        if (selectedUserType?.value && parentEntityTypeKey) {
            isExisting ? loadUserTypeParentEntityTypeStatuses() : loadParentEntityTypeStatuses(parentEntityTypeKey);
        }
    }, [selectedUserType?.value, isExisting]);

    useEffect(() => {
        loadDTSRData();
    }, []);

    const loadUserTypes = async (parentEntityTypeKey) => {
        const userTypes = await getUserTypes(parentEntityTypeKey);
        setUserTypes(userTypes);
    };

    const loadParentEntityTypeStatuses = async (parentEntityTypeKey) => {
        const parentEntityTypeStatuses = await getParentEntityTypeStatuses(parentEntityTypeKey);
        setParentEntityTypeStatuses(parentEntityTypeStatuses);
    };

    const saveAllValues = async (shouldExit) => {
        const updatedDocumentType = {};

        updatedDocumentType.name = newDocumentName;
        updatedDocumentType.description = get(documentType, "description");
        updatedDocumentType.multipleAllowed = get(documentType, "multipleAllowed");
        updatedDocumentType.parentEntityTypeKey = get(documentType, "parentEntityType.parentEntityTypeKey");
        updatedDocumentType.parentEntityTypeStatusDTOs = parentEntityTypeStatuses;
        if (selectedUserType?.userType === "USER_ROLE") {
            updatedDocumentType.userRoleKey = selectedUserType.userTypeKey;
        } else if (selectedUserType?.userType === "USER_CONTEXT") {
            updatedDocumentType.userContextKey = selectedUserType.userTypeKey;
        }

        setIsSaving(true);
        const result = await handleSaveAll(documentTypeId, updatedDocumentType);
        setIsSaving(false);

        if (result.success && (shouldExit || !isExisting)) {
            const path = shouldExit ? "/document-type" : `/document-type/document/${result.documentTypeKey}`;
            navigate(path);
        }
    };

    const handleDeleteCancelDocumentType = async () => {
        const currentPath = location.pathname;
        if (currentPath === "/document-type/create") {
            navigate("/document-type");
        } else {
            setIsDeleting(true);
            const result = await handleDeleteDocumentType(documentTypeId);
            setIsDeleting(false);
            if (result.success) {
                navigate("/document-type");
            }
        }
    };

    const handleDocumentNameChange = (v) => {
        setNewDocumentName(v);
    };

    const handleInputChange = (k, v) => {
        setDocumentType((prevState) => ({
            ...prevState,
            [k]: v,
        }));
    };

    const changeParentEntity = async (e) => {
        const newParentEntity =
            parentEntityTypesList.find((entity) => entity.parentEntityTypeKey === Number(e.target.value)) ||
            documentType.parentEntity;

        handleInputChange(e.target.id, newParentEntity);

        if (newParentEntity?.parentEntityTypeKey) {
            await loadUserTypes(newParentEntity.parentEntityTypeKey);
            await loadParentEntityTypeStatuses(newParentEntity.parentEntityTypeKey);
        }
    };

    const changeUserType = (e) => {
        const selectedUserType = userTypes.find((userType) => userType.value === e.target.value);
        setSelectedUserType(selectedUserType);
    };

    const handleRowUpdate = (updatedRow) => {
        setParentEntityTypeStatuses((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
    };

    const loadUserTypeParentEntityTypeStatuses = async () => {
        let userRoleKey = null;
        let userContextKey = null;
        if (selectedUserType?.userType === "USER_ROLE") {
            userRoleKey = selectedUserType.userTypeKey;
        } else if (selectedUserType?.userType === "USER_CONTEXT") {
            userContextKey = selectedUserType.userTypeKey;
        }
        const parentEntityTypeStatuses = await getUserTypeParentEntityStatuses(
            userRoleKey,
            userContextKey,
            documentTypeId,
            parentEntityTypeKey
        );
        setParentEntityTypeStatuses(parentEntityTypeStatuses);
    };

    return (
        <React.Fragment>
            <StyledPageToolbar>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <StyledGoBackLink onClick={() => navigate(-1)}>
                        <LeftChevron />
                    </StyledGoBackLink>
                    <div>
                        <h2 className="page-title">{dataLoaded ? subHeaderText : "Document"}</h2>
                        <p className="page-subtitle">{get(documentType, "name", "")}</p>
                    </div>
                </div>
                {dataLoaded && knownDocument && (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        {parentEntityTypeKey.length !== 0 && (
                            <Link to={`/parent-type/${parentEntityTypeKey}`} style={{ textDecoration: "none" }}>
                                <Button variant="contained" color="secondary" disabled={isSaving || isDeleting}>
                                    Edit Rules
                                </Button>
                            </Link>
                        )}
                        <SaveAndExitButton
                            disabled={isSaving || isDeleting}
                            onSave={() => saveAllValues(false)}
                            onSaveAndExit={() => saveAllValues(true)}
                        />
                        <StyledDeleteButton
                            variant="contained"
                            disabled={isSaving || isDeleting}
                            onClick={() => handleDeleteCancelDocumentType()}
                        >
                            {deleteCancelText}
                        </StyledDeleteButton>
                    </div>
                )}
            </StyledPageToolbar>
            {!dataLoaded ? (
                <StyledLoadingSpinner>
                    <CircularProgress />
                </StyledLoadingSpinner>
            ) : knownDocument ? (
                <div>
                    <Accordion defaultExpanded style={{ marginBottom: "10px" }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <CheckCircle className="waterloo" /> Document Information
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails>
                            <div>
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <div style={{ flex: "1 1 0" }}>
                                        <BSInput
                                            id="name"
                                            label="Name"
                                            placeholder="Enter document name..."
                                            value={newDocumentName}
                                            onChange={(e) => handleDocumentNameChange(e.target.value)}
                                            errorText={"Required"}
                                            error={get(errors, "name", "")}
                                        />
                                        <BSSelect
                                            id="parentEntityType"
                                            label="Parent Type"
                                            value={documentType?.parentEntityType?.parentEntityTypeKey || ""}
                                            onChange={changeParentEntity}
                                            options={parentEntityTypesToOptionsMap}
                                            disabled={documentTypeId !== undefined}
                                        />
                                    </div>
                                    <div style={{ flex: "1 1 0" }}>
                                        <BSInput
                                            id="description"
                                            label="Description"
                                            placeholder="Enter document description..."
                                            value={documentType.description}
                                            onChange={(e) => {
                                                handleInputChange(e.target.id, e.target.value);
                                            }}
                                            multiline
                                            rows={5}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
                                    <h3>Document Attachment Permissions</h3>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "8px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <BSSelect
                                            id="userType"
                                            label="User Type"
                                            value={selectedUserType.value || ""}
                                            onChange={(e) => {
                                                changeUserType(e);
                                            }}
                                            options={userTypes}
                                            disabled={documentType?.parentEntityType?.parentEntityTypeKey === undefined}
                                            extraFormControlStyles={{ flex: "1 1 0" }}
                                        />
                                        <div style={{ flex: "1 1 0" }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={documentType.multipleAllowed ? true : false}
                                                        onChange={(e) =>
                                                            handleInputChange(e.target.value, e.target.checked)
                                                        }
                                                        value="multipleAllowed"
                                                        color="primary"
                                                    />
                                                }
                                                label="Multiple Attachments Allowed"
                                            />
                                        </div>
                                    </div>
                                </div>
                                {parentEntityTypeStatuses && (
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <DocumentTypeAdminDataGrid
                                            key={"uploadGrid"}
                                            rows={parentEntityTypeStatuses}
                                            uploadGrid
                                            onRowUpdate={handleRowUpdate}
                                        />
                                        <DocumentTypeAdminDataGrid
                                            key={"readGrid"}
                                            rows={parentEntityTypeStatuses}
                                            readGrid
                                            onRowUpdate={handleRowUpdate}
                                        />
                                        <DocumentTypeAdminDataGrid
                                            key={"deleteGrid"}
                                            rows={parentEntityTypeStatuses}
                                            deleteGrid
                                            onRowUpdate={handleRowUpdate}
                                        />
                                    </div>
                                )}
                            </div>
                        </AccordionDetails>
                    </Accordion>
                    {documentTypeId && (
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <CheckCircle className="waterloo" />
                                Compliance Rules Impacting Document Type
                            </AccordionSummary>
                            <Divider />
                            <StyledComplianceRules>
                                <ComplianceRulesByDocumentTypeContainer {...props} />
                            </StyledComplianceRules>
                        </Accordion>
                    )}
                </div>
            ) : (
                <ErrorMessage Icon={ErrorOutline} message="Document Could Not Be Found" />
            )}
        </React.Fragment>
    );
};

export default DocumentTypeAdmin;
